/* KD product page: delivery bar, design label, size chart, WhatsApp CS, photo swatches,
   sold-out ordering and "choose a size first". Labels come from kd-pdp-data (same keys as quick view). */
(() => {
  const dataElement = document.querySelector('script[data-kd-pdp-data]');
  const info = document.querySelector('product-info[id^="MainProduct-"]');
  if (!dataElement || !info) return;
  const data = JSON.parse(dataElement.textContent);
  const labels = data.labels || {};
  const sectionId = info.dataset.section;
  const form = document.getElementById(`product-form-${sectionId}`);

  const decode = (text) => {
    const box = document.createElement('textarea');
    box.innerHTML = text || '';
    return box.value;
  };

  // Estimated delivery: today + three Monday-Friday business days in Hong Kong (same rule as quick view).
  const deliveryDate = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit', day: '2-digit'})
      .formatToParts(new Date())
      .reduce((result, part) => {
        if (part.type !== 'literal') result[part.type] = Number(part.value);
        return result;
      }, {});
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
    let businessDays = 0;
    while (businessDays < 3) {
      date.setUTCDate(date.getUTCDate() + 1);
      const weekday = date.getUTCDay();
      if (weekday !== 0 && weekday !== 6) businessDays += 1;
    }
    const language = document.documentElement.lang?.toLowerCase().startsWith('zh') ? 'zh-HK' : 'en-HK';
    return new Intl.DateTimeFormat(language, {month: 'long', day: 'numeric', timeZone: 'UTC'}).format(date);
  };

  // Free-delivery basket (any Global Scenery item, or over the threshold) replaces the fee/threshold lines.
  const deliveryMessages = (cart) => {
    const estimate = labels.delivery_estimate?.replace('[date]', deliveryDate());
    const threshold = Number(data.freeDeliveryThreshold) * 100;
    const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
    const basketFree = cart && (
      cart.items?.some((item) => item.handle?.includes('global-scenery')) ||
      (threshold > 0 && cart.total_price / rate >= threshold)
    );
    let list = [estimate, labels.delivery_fee, labels.free_delivery];
    if (basketFree && labels.free_delivery_applied) list = [estimate, labels.free_delivery_applied];
    else if (data.freeDeliveryProduct && labels.free_delivery_scenery) list = [estimate, labels.free_delivery_scenery];
    return list.filter(Boolean).map(decode);
  };

  const addDeliveryBar = () => {
    const tax = info.querySelector('.product__tax');
    let messages = deliveryMessages(null);
    if (!tax || !messages.length) return;
    const bar = document.createElement('aside');
    bar.className = 'kd-pdp-delivery';
    bar.setAttribute('aria-live', 'polite');
    const message = document.createElement('span');
    message.textContent = messages[0];
    bar.append(message);
    tax.replaceWith(bar);
    let current = 0;
    let request = 0;
    const refresh = () => {
      const requestId = ++request;
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
        .then((response) => response.json())
        .then((cart) => {
          if (requestId !== request) return;
          const next = deliveryMessages(cart);
          if (!next.length || next.join('|') === messages.join('|')) return;
          messages = next;
          current = Math.min(current, messages.length - 1);
          message.textContent = messages[current];
        })
        .catch(() => {});
    };
    document.addEventListener('afterCartChanged', refresh);
    document.addEventListener('afterUpdateQuantity', refresh);
    refresh();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    window.setInterval(() => {
      if (messages.length < 2) return;
      current = (current + 1) % messages.length;
      message.classList.add('is-changing');
      window.setTimeout(() => {
        message.textContent = messages[current % messages.length];
        message.classList.remove('is-changing');
      }, 180);
    }, 4200);
  };

  const addDesignLabel = () => {
    const title = info.querySelector('.product__title');
    if (!title || !data.designCollection) return;
    info.querySelectorAll('.product__vendor-link').forEach((vendor) => vendor.closest('.product__text, p, div')?.classList.add('kd-pdp-hidden'));
    const label = document.createElement(data.designCollectionUrl ? 'a' : 'span');
    label.className = 'kd-pdp-design notranslate';
    label.setAttribute('translate', 'no');
    label.textContent = data.designCollection;
    if (data.designCollectionUrl) label.href = data.designCollectionUrl;
    title.insertAdjacentElement('beforebegin', label);
  };

  const fieldsetFor = (position) => {
    if (!position) return null;
    const input = info.querySelector(`input[type="radio"][id$="-${position}-0"]`);
    return input ? input.closest('fieldset') : null;
  };

  const addSizeChart = (sizeFieldset) => {
    if (!sizeFieldset || !labels.size_chart) return;
    const sizeTab = document.querySelector('[role="tab"][data-tab="sizeguide"]');
    let action;
    if (data.hasSizeImages && sizeTab) {
      action = document.createElement('button');
      action.type = 'button';
      action.addEventListener('click', () => {
        sizeTab.click();
        sizeTab.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
        sizeTab.focus({preventScroll: true});
      });
    } else if (data.sizePageUrl) {
      action = document.createElement('a');
      action.href = data.sizePageUrl;
      action.target = '_blank';
      action.rel = 'noopener';
    } else {
      return;
    }
    action.className = 'kd-pdp-size-chart';
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '↔';
    action.append(icon, document.createTextNode(decode(labels.size_chart)));
    const legend = sizeFieldset.querySelector('legend');
    const row = document.createElement('div');
    row.className = 'kd-pdp-legend-row';
    legend.insertAdjacentElement('beforebegin', row);
    row.append(legend, action);
  };

  const addSwatches = (colourFieldset) => {
    if (!colourFieldset || !data.swatches) return;
    colourFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      const url = data.swatches[input.value];
      const label = colourFieldset.querySelector(`label[for="${input.id}"]`);
      if (!url || !label || label.querySelector('.kd-pdp-swatch')) return;
      const swatch = document.createElement('span');
      swatch.className = 'kd-pdp-swatch';
      swatch.setAttribute('aria-hidden', 'true');
      swatch.style.backgroundImage = `url("${url}")`;
      label.classList.add('kd-pdp-has-swatch');
      label.prepend(swatch);
    });
  };

  // Keep every row's sold-out choices after the available ones; re-run when the theme updates availability.
  const orderSoldOut = (fieldset) => {
    if (!fieldset) return;
    const list = fieldset.querySelector('ul');
    if (!list) return;
    const items = [...list.children];
    const soldOut = items.filter((item) => item.querySelector('label.soldout, input.disabled'));
    const available = items.filter((item) => !soldOut.includes(item));
    const ordered = [...available, ...soldOut];
    if (ordered.every((item, index) => list.children[index] === item)) return;
    ordered.forEach((item) => list.append(item));
  };

  // No size is pre-selected when the customer arrives without ?variant=.
  let sizeChosen = true;
  const requireSize = (sizeFieldset) => {
    if (!sizeFieldset || data.sizeCount < 2 || new URLSearchParams(location.search).has('variant')) return;
    sizeChosen = false;
    sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = false; });
    const value = sizeFieldset.querySelector('.product-form__input__option-value');
    if (value) value.textContent = '';
    // Variant rendering replaces the picker; listen on the stable document in capture phase.
    document.addEventListener('change', (event) => {
      const currentSizeFieldset = fieldsetFor(data.sizePosition);
      const stickySizeId = `Option-${sectionId}-${data.sizePosition - 1}-sticky`;
      if (!currentSizeFieldset?.contains(event.target) && event.target.id !== stickySizeId) return;
      sizeChosen = true;
      currentSizeFieldset?.classList.remove('kd-pdp-needs-size');
    }, true);
    const stop = (event) => {
      if (sizeChosen) return;
      const target = event.target.closest('[name="add"], .shopify-payment-button, .kd-buy-now, .kd-sticky-buy-now, .sticky-cart__submit');
      if (!target && event.type === 'click') return;
      const purchaseForm = (target || event.target).closest('form');
      if (!purchaseForm || ![`product-form-${sectionId}`, `product-form-Sticky-${sectionId}`].includes(purchaseForm.id)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const currentSizeFieldset = fieldsetFor(data.sizePosition);
      if (!currentSizeFieldset) return;
      currentSizeFieldset.classList.remove('kd-pdp-needs-size');
      void currentSizeFieldset.offsetWidth;
      currentSizeFieldset.classList.add('kd-pdp-needs-size');
      currentSizeFieldset.scrollIntoView({behavior: 'smooth', block: 'center'});
      currentSizeFieldset.querySelector('input[type="radio"]:not(.disabled):not(:disabled)')?.focus({preventScroll: true});
    };
    document.addEventListener('click', stop, true);
    document.addEventListener('submit', stop, true);
    const note = document.createElement('p');
    note.className = 'kd-pdp-size-note';
    note.textContent = decode(labels.choose_options);
    sizeFieldset.append(note);
  };

  const addWhatsApp = () => {
    const buttons = info.querySelector('.product__sale-box__actions') || info.querySelector('.product-form__buttons');
    if (!buttons || !labels.whatsapp) return;
    const link = document.createElement('a');
    link.className = 'kd-pdp-whatsapp';
    link.target = '_blank';
    link.rel = 'noopener';
    link.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 3.5a8.5 8.5 0 0 0-7.3 12.8L3.5 20.5l4.3-1.1A8.5 8.5 0 1 0 12 3.5Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M9 8.5c.2-.5.5-.5.8-.5h.5c.2 0 .4.1.5.4l.6 1.4c.1.3 0 .5-.1.7l-.5.6c.5 1 1.3 1.8 2.3 2.3l.6-.5c.2-.2.4-.2.7-.1l1.4.6c.3.1.4.3.4.5v.5c0 .3-.1.6-.5.8-.6.3-1.6.4-3.2-.4a8 8 0 0 1-3.2-3.2c-.8-1.6-.7-2.6-.3-3.1Z" fill="currentColor"/></svg>';
    const text = document.createElement('span');
    text.textContent = decode(labels.whatsapp);
    link.append(text);
    const refresh = () => {
      const variantId = form?.querySelector('input[name="id"]')?.value;
      const template = decode(labels.whatsapp_interest || 'I am interested in "[product]" "[sku]"');
      const message = template.replace('[product]', data.productTitle || '').replace('[sku]', data.variantSkus?.[variantId] || '');
      link.href = `https://wa.me/85263649970?text=${encodeURIComponent(message)}`;
    };
    refresh();
    link.addEventListener('click', refresh);
    const share = info.querySelector('share-button, .share-button');
    if (share) {
      const row = document.createElement('div');
      row.className = 'kd-pdp-actions';
      // Same container as Add to cart / Buy now, so both rows share one width.
      (info.querySelector('.product__sale-box__actions') || share.parentElement).append(row);
      row.append(link, share);
    } else {
      buttons.insertAdjacentElement('afterend', link);
    }
  };

  const colourFieldset = fieldsetFor(data.colourPosition);
  const sizeFieldset = fieldsetFor(data.sizePosition);
  addDeliveryBar();
  addDesignLabel();
  addSwatches(colourFieldset);
  addSizeChart(sizeFieldset);
  requireSize(sizeFieldset);
  [colourFieldset, sizeFieldset].forEach(orderSoldOut);
  addWhatsApp();
  info.addEventListener('change', () => window.setTimeout(() => [fieldsetFor(data.colourPosition), fieldsetFor(data.sizePosition)].forEach(orderSoldOut), 400));
})();
